"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { type ChatStatus, isToolUIPart } from "ai";
import { InfoIcon } from "lucide-react";
import { type CSSProperties, memo, type ReactElement, useMemo } from "react";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageAvatar,
  MessageContent,
} from "@/components/ai-elements/message";
import { useTrackEvent } from "@/components/analytics/events";
import { CopyToClipboardButton } from "@/components/ui/copy-to-clipboard-button";
import type { ChatToolPartPlugin } from "@/lib/ai/chat-client-plugin";
import type {
  ChatAddToolApprovalResponse,
  ChatAddToolResult,
  ChatLogContext,
  ChatRetryApprovalContinuation,
  ChatSendMessage,
} from "@/lib/ai/chat-message-renderer-contract";
import { ActivityGroup } from "@/lib/ai/message-view/activity-group";
import { AnswerGroup } from "@/lib/ai/message-view/answer-group";
import { AttachmentsGroup } from "@/lib/ai/message-view/attachments-group";
import {
  deriveMessageViewPhase,
  hasFinalResponseStarted,
  hasPendingToolExecution,
  isMessageViewPhaseOngoing,
} from "@/lib/ai/message-view/derive-message-view-phase";
import { getToolRenderPlans } from "@/lib/ai/message-view/get-tool-render-plans";
import { groupMessageParts } from "@/lib/ai/message-view/group-message-parts";
import { getMessagePartRenderSignature } from "@/lib/ai/message-view/part-signature";
import type {
  ActivityRenderGroup,
  ActivityToolDisclosure,
  ActivityToolPresentation,
  MessageRenderGroup,
} from "@/lib/ai/message-view/render-model";
import { StandaloneToolPart } from "@/lib/ai/message-view/standalone-tool-part";
import { ToolActivityRow } from "@/lib/ai/message-view/tool-activity-row";
import { ToolFailureObserver } from "@/lib/ai/message-view/tool-failure-observer";
import { ToolRouteRefreshObserver } from "@/lib/ai/message-view/tool-route-refresh-observer";
import type { AgentUIMessage } from "@/lib/ai/types";
import { cn } from "@/lib/utils";

/** message の描画に必要な実行状態と Tool 応答 handler をまとめた props。 */
export type ChatMessageRendererProps = {
  messages: AgentUIMessage[];
  /** Show recorded response IDs only when the host authorizes global admin access. */
  showResponseModel?: boolean;
  creatorName?: string;
  creatorPicture?: string | null;
  addToolResult: ChatAddToolResult;
  addToolApprovalResponse: ChatAddToolApprovalResponse;
  /** confirmation response 後に、batch完了条件を検証して継続する。 */
  requestApprovalContinuation: ChatSendMessage;
  /** 通常メッセージ用の直列送信経路。 */
  sendMessage: ChatSendMessage;
  canRetryApprovalContinuation?: boolean;
  isRetryingApprovalContinuation?: boolean;
  retryApprovalContinuation?: ChatRetryApprovalContinuation;
  retryableApprovalToolCallId?: string;
  chatStatus: ChatStatus;
  isPersistingMessages: boolean;
  copyLabel: string;
  toolPartPlugins: ChatToolPartPlugin[];
  logContext?: ChatLogContext;
  responseStartedAtMs?: number;
};

type ChatMessageRowProps = Omit<
  ChatMessageRendererProps,
  "messages" | "chatStatus"
> & {
  message: AgentUIMessage;
  renderChatStatus: ChatStatus;
  isLastMessage: boolean;
};

const CHAT_MESSAGE_ROW_TEXT_INTRINSIC_SIZE = "auto 160px";
const CHAT_MESSAGE_ROW_TOOL_INTRINSIC_SIZE = "auto 520px";

/** 通常回答として表示している text だけを clipboard 用文字列へ直列化する。 */
function buildVisibleMessageCopyText(groups: MessageRenderGroup[]): string {
  return groups
    .filter((group) => group.kind === "answer")
    .flatMap((group) => group.parts)
    .map(({ part }) => part.text ?? "")
    .join("\n\n")
    .trim();
}

/** Activity 外で実際に描画される応答内容があるかを判定する。 */
function hasVisibleMessageResponse(groups: MessageRenderGroup[]): boolean {
  return groups.some((group) => {
    if (group.kind === "answer") {
      return group.parts.some(({ part }) => part.text.trim().length > 0);
    }
    return group.kind === "standalone" || group.kind === "attachments";
  });
}

function buildCompletedMessageOcclusionStyle({
  hasToolPart,
  isLastMessage,
}: {
  hasToolPart: boolean;
  isLastMessage: boolean;
}): CSSProperties | undefined {
  if (isLastMessage) return undefined;
  return {
    contentVisibility: "auto",
    containIntrinsicSize: hasToolPart
      ? CHAT_MESSAGE_ROW_TOOL_INTRINSIC_SIZE
      : CHAT_MESSAGE_ROW_TEXT_INTRINSIC_SIZE,
  };
}

function areChatMessageRowsEqual(
  previous: ChatMessageRowProps,
  next: ChatMessageRowProps,
): boolean {
  const hasSameMessage =
    previous.message === next.message ||
    (previous.message.id === next.message.id &&
      previous.message.role === next.message.role &&
      previous.message.metadata?.completionCause ===
        next.message.metadata?.completionCause &&
      previous.message.parts.length === next.message.parts.length &&
      previous.message.parts.every(
        (part, index) =>
          getMessagePartRenderSignature(part) ===
          getMessagePartRenderSignature(
            next.message.parts[index] as typeof part,
          ),
      ));

  return (
    hasSameMessage &&
    previous.message.metadata?.modelId === next.message.metadata?.modelId &&
    previous.message.metadata?.completionCause ===
      next.message.metadata?.completionCause &&
    previous.creatorName === next.creatorName &&
    previous.creatorPicture === next.creatorPicture &&
    previous.addToolResult === next.addToolResult &&
    previous.addToolApprovalResponse === next.addToolApprovalResponse &&
    previous.sendMessage === next.sendMessage &&
    previous.canRetryApprovalContinuation ===
      next.canRetryApprovalContinuation &&
    previous.isRetryingApprovalContinuation ===
      next.isRetryingApprovalContinuation &&
    previous.retryApprovalContinuation === next.retryApprovalContinuation &&
    previous.retryableApprovalToolCallId === next.retryableApprovalToolCallId &&
    previous.renderChatStatus === next.renderChatStatus &&
    previous.isPersistingMessages === next.isPersistingMessages &&
    previous.isLastMessage === next.isLastMessage &&
    previous.copyLabel === next.copyLabel &&
    previous.showResponseModel === next.showResponseModel &&
    previous.toolPartPlugins === next.toolPartPlugins &&
    previous.logContext === next.logContext &&
    previous.responseStartedAtMs === next.responseStartedAtMs
  );
}

/** chat message 一覧を共通 renderer として描画する。 */
export function ChatMessageRenderer({
  messages,
  chatStatus,
  ...rowProps
}: ChatMessageRendererProps): ReactElement {
  const lastMessageId = messages.at(-1)?.id;
  return (
    <div className="flex flex-col">
      {messages.map((message, index) => (
        <ChatMessageRow
          {...rowProps}
          isLastMessage={index === messages.length - 1}
          key={message.id}
          message={message}
          renderChatStatus={message.id === lastMessageId ? chatStatus : "ready"}
        />
      ))}
    </div>
  );
}

const ChatMessageRow = memo(function ChatMessageRow({
  message,
  creatorName,
  creatorPicture,
  addToolResult,
  addToolApprovalResponse,
  requestApprovalContinuation,
  sendMessage,
  canRetryApprovalContinuation,
  isRetryingApprovalContinuation,
  retryApprovalContinuation,
  retryableApprovalToolCallId,
  renderChatStatus,
  isPersistingMessages,
  isLastMessage,
  copyLabel,
  showResponseModel,
  toolPartPlugins,
  logContext,
  responseStartedAtMs,
}: ChatMessageRowProps): ReactElement {
  const trackEvent = useTrackEvent();
  const { i18n } = useLingui();
  const messageToolPartPlugins = useMemo(
    () =>
      toolPartPlugins.map((plugin) => ({
        plugin,
        messageState: plugin.getMessageState?.(message),
      })),
    [message, toolPartPlugins],
  );
  const toolPlans = useMemo(
    () =>
      getToolRenderPlans({
        message,
        chatStatus: renderChatStatus,
        isLastMessage,
        messageToolPartPlugins,
      }),
    [isLastMessage, message, messageToolPartPlugins, renderChatStatus],
  );
  const groups = useMemo(
    () => groupMessageParts(message, toolPlans),
    [message, toolPlans],
  );
  const viewState = useMemo(
    () =>
      deriveMessageViewPhase(
        message,
        groups,
        {
          isLatestMessage: isLastMessage,
          chatStatus: renderChatStatus,
          completionCause: message.metadata?.completionCause ?? undefined,
        },
        toolPlans,
      ),
    [groups, isLastMessage, message, renderChatStatus, toolPlans],
  );
  const copyText = useMemo(() => buildVisibleMessageCopyText(groups), [groups]);
  const hasToolPart = message.parts.some((part) => isToolUIPart(part as never));
  const isOngoing = isMessageViewPhaseOngoing(viewState.phase);
  // 処理中のスクロール余地だけを確保し、完了後の短い回答に大きな空白を残さない。
  const shouldReserveStreamingViewport =
    message.role === "assistant" && isLastMessage && isOngoing;
  const showCopy =
    copyText.length > 0 &&
    (message.role === "user" || (message.role === "assistant" && !isOngoing));
  const activity = groups.find(
    (group): group is ActivityRenderGroup => group.kind === "activity",
  );
  const visibleActivity =
    activity ??
    (isOngoing
      ? {
          kind: "activity" as const,
          steps: [],
          toolCount: 0,
          hasReasoning: false,
        }
      : null);
  const finalResponseStarted = hasFinalResponseStarted(groups, toolPlans);
  const activityHasVisibleAnswer =
    finalResponseStarted && !hasPendingToolExecution(toolPlans);
  const hasNoVisibleResponse = !hasVisibleMessageResponse(groups);

  const renderTool = (
    partIndex: number,
    presentation: ActivityToolPresentation,
    disclosure?: ActivityToolDisclosure,
  ) => {
    const plan = toolPlans.get(partIndex);
    return plan ? (
      <ToolActivityRow
        disclosure={disclosure}
        isStreaming={renderChatStatus === "streaming"}
        key={plan.id}
        plan={plan}
        presentation={presentation}
      />
    ) : null;
  };

  return (
    <Message
      className={cn(
        message.role === "user" && showCopy && "pb-10",
        shouldReserveStreamingViewport &&
          "min-h-[max(12rem,calc(100cqh-16rem))]",
      )}
      data-chat-message-row={message.id}
      data-chat-message-phase={viewState.phase}
      from={message.role}
      style={buildCompletedMessageOcclusionStyle({
        hasToolPart,
        isLastMessage,
      })}
    >
      {message.role === "user" ? (
        <MessageAvatar
          name={creatorName || ""}
          src={creatorPicture ?? undefined}
        />
      ) : null}
      <div
        className={`relative flex min-w-0 flex-col gap-1.5 ${message.role === "assistant" ? "w-full max-w-full flex-1 items-stretch" : "w-fit max-w-[min(80%,56ch)] flex-none items-end"}`}
      >
        <MessageContent
          className={
            message.role === "assistant" ? "w-full max-w-full" : "max-w-full"
          }
          data-chat-disclaimer-occluder={
            message.role === "assistant" && isLastMessage
              ? "content"
              : undefined
          }
          variant="contained"
        >
          {[...toolPlans.values()].map((plan) => (
            <ToolFailureObserver
              key={`failure-${plan.id}`}
              logContext={logContext}
              plan={plan}
            />
          ))}
          {[...toolPlans.values()].map((plan) => (
            <ToolRouteRefreshObserver
              chatStatus={renderChatStatus}
              isPersistingMessages={isPersistingMessages}
              key={`route-refresh-${plan.id}`}
              plan={plan}
            />
          ))}
          {visibleActivity ? (
            <ActivityGroup
              group={visibleActivity}
              hasVisibleAnswer={activityHasVisibleAnswer}
              initialStartedAtMs={
                isLastMessage ? responseStartedAtMs : undefined
              }
              phase={viewState.phase}
              renderTool={renderTool}
            />
          ) : viewState.phase === "incomplete" && hasNoVisibleResponse ? (
            <span className="flex items-center text-muted-foreground">
              <InfoIcon className="inline-block size-4" />
              {i18n._(msg`Response interrupted`)}
            </span>
          ) : null}
          {groups.map((group, groupIndex) => {
            if (group.kind === "activity" || group.kind === "sources")
              return null;
            if (group.kind === "answer") {
              return (
                <AnswerGroup
                  group={group}
                  key={`answer-${groupIndex}`}
                  phase={viewState.phase}
                />
              );
            }
            if (group.kind === "attachments") {
              return (
                <AttachmentsGroup
                  group={group}
                  key={`attachments-${groupIndex}`}
                />
              );
            }
            const plan = toolPlans.get(group.partIndex);
            return plan ? (
              <StandaloneToolPart
                addToolApprovalResponse={addToolApprovalResponse}
                addToolResult={addToolResult}
                isStreaming={renderChatStatus === "streaming"}
                canRetryApprovalContinuation={canRetryApprovalContinuation}
                isRetryingApprovalContinuation={isRetryingApprovalContinuation}
                key={plan.id}
                plan={plan}
                requestApprovalContinuation={requestApprovalContinuation}
                sendMessage={sendMessage}
                retryApprovalContinuation={retryApprovalContinuation}
                retryableApprovalToolCallId={retryableApprovalToolCallId}
              />
            ) : null;
          })}
          {viewState.phase === "incomplete" && !hasNoVisibleResponse ? (
            <span
              className="flex items-center gap-1.5 text-muted-foreground"
              data-chat-interruption-notice=""
            >
              <InfoIcon className="size-4 shrink-0" />
              {i18n._(msg`Response interrupted`)}
            </span>
          ) : null}
        </MessageContent>
        {showResponseModel === true &&
        message.role === "assistant" &&
        typeof message.metadata?.modelId === "string" &&
        message.metadata.modelId.trim().length > 0 ? (
          <span
            className="max-w-full break-all text-xs text-muted-foreground"
            data-chat-response-model=""
          >
            {i18n._(msg`Model:`)} {message.metadata.modelId}
          </span>
        ) : null}
        {showCopy ? (
          <MessageActions
            data-chat-disclaimer-occluder={
              message.role === "assistant" && isLastMessage
                ? "actions"
                : undefined
            }
            className={`h-6 min-h-6 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${
              message.role === "user"
                ? "absolute top-full right-0 mt-1.5 opacity-0"
                : "self-start opacity-60"
            }`}
          >
            <MessageAction asChild tooltip={copyLabel}>
              <CopyToClipboardButton
                className="size-7"
                onCopied={() => {
                  if (message.role === "assistant") {
                    trackEvent({
                      eventName: "ai_response_exported",
                      payload: { export_format: "copy" },
                    });
                  }
                }}
                showToast={false}
                showTooltip={false}
                size="icon-sm"
                textToCopy={copyText}
                tooltipText={copyLabel}
                variant="ghost"
              />
            </MessageAction>
          </MessageActions>
        ) : null}
      </div>
    </Message>
  );
}, areChatMessageRowsEqual);
